/**
 * queueExit — LAYER 3 of the offline queue: HOW A REP GETS AN ENTRY OUT.
 *
 * The queue had no exit (BUG-2026-07-14-VC559). An entry could be, simultaneously:
 *   - un-syncable  — a deterministic 4xx, pinned at maxRetries forever;
 *   - un-purgeable — the dead-letter branch tests `!isCritical(type)`, and NOTHING
 *                    is ephemeral, so that branch is unreachable by configuration;
 *   - un-retryable — `retryFailedEntries` reset it to pending and immediately
 *                    re-pinned it on the same deterministic failure, reporting
 *                    nothing; and
 *   - un-deletable — no `dequeue` was reachable from any UI.
 *
 * "Never lose the data" had been built as "never let go of it", and the result was a
 * red banner the operator could not clear over a queue he could not empty.
 *
 * The two exits, and their invariants:
 *   RETRY   — only for entries a retry could plausibly change. It NEVER silently
 *             re-pins a known-deterministic failure; it says nothing changed.
 *   DISCARD — always available, but ONLY after the payload is CONFIRMED landed
 *             off-device. If the durable sink does not confirm, the entry STAYS.
 *             We would rather leave a rep with a stuck row than delete their only
 *             copy of a visit report on the strength of a call that "probably" wrote.
 *
 * `queueExit.test.ts` iterates QUEUE_FAILURE_REASONS and proves every terminal state
 * has an exit that STRICTLY reduces the queue.
 */

import { captureFailedWriteConfirmed } from './failedWriteCapture'
import { flushQueue } from './queueFlush'
import { readQueue, isGenuinelyFailed, isRetryableReason, isCritical } from './queueStore'
import { updateQueue } from './queueStoreWrite'
import { rePendDependentsBehindPendingPredecessors, dependentsOrphanedByRemoval, recordDiscardedPredecessor } from './queueOrdering'
import { applyPredecessorFailure } from './replayFailure'
import type {
  QueueEntry,
  QueueEntryView,
  RetryFailedResult,
  DiscardOutcome,
} from './offlineQueueTypes'

// `isRetryableReason` MOVED DOWN to queueStore (LAYER 1) so the on-load recovery
// (queueRecovery, driven from queueFlush) can share this ONE classifier without the
// import cycle queueFlush → queueRecovery → queueExit → queueFlush that madge would
// reject. Re-exported here so this module's public surface is unchanged: every
// existing `from './queueExit'` / facade import keeps resolving.
export { isRetryableReason } from './queueStore'

function toView(entry: QueueEntry): QueueEntryView {
  return {
    id: entry.id,
    type: entry.type,
    createdAt: entry.createdAt,
    lastAttempt: entry.lastAttempt,
    retries: entry.retries,
    maxRetries: entry.maxRetries,
    status: entry.status,
    failureReason: entry.failureReason,
    finalCode: entry.finalCode,
    retryable: isRetryableReason(entry.failureReason),
  }
}

/**
 * Every entry a rep may act on, newest first — identity + fate, never the raw
 * payload. This is what a UI needs to offer a per-entry exit, and it is what the
 * queue never exposed.
 */
export async function describeQueueEntries(): Promise<QueueEntryView[]> {
  const queue = await readQueue()
  return queue
    // Drop the synced sentinel (failed + retries remaining = synced, pending removal).
    .filter(e => !(e.status === 'failed' && e.retries < e.maxRetries))
    .map(toView)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
}

/**
 * Reset the retryable failed entries to pending and flush.
 *
 * HONEST NO-OP (D-33 R1): when every failed entry is a known-deterministic failure,
 * this resets NOTHING, does NOT flush, and returns `retried: 0` with
 * `skipped: 'nothing-retryable'`. The old code reset them all, re-flushed into the
 * identical rejection, re-pinned them within the same tick, and returned a
 * `{synced:0, failed:N}` that looked exactly like a transient hiccup. The rep pressed
 * the button, the spinner span, and the state was bit-for-bit unchanged — forever.
 */
export async function retryFailedEntries(): Promise<RetryFailedResult> {
  // The reset runs on the CURRENT queue inside the serialised chain (queueStoreWrite), so a
  // flush in flight cannot write its stale snapshot over it. The flush below runs AFTER the
  // step settles — calling it inside the step would wait on itself.
  const counts = await updateQueue(queue => {
    let retried = 0
    let notRetryable = 0

    for (const entry of queue) {
      if (!isGenuinelyFailed(entry)) continue
      if (!isRetryableReason(entry.failureReason)) {
        notRetryable++
        continue
      }
      entry.status = 'pending'
      entry.retries = 0
      delete entry.failureReason
      retried++
    }

    // A dependent pinned behind a dead predecessor moves ONLY with that predecessor: the
    // loop above counted it not-retryable; the ones whose predecessor was just re-pended
    // follow it back into the flush lane (it replays strictly after — queueOrdering).
    const dependentsMoved = rePendDependentsBehindPendingPredecessors(queue)
    retried += dependentsMoved
    notRetryable -= dependentsMoved

    const result = { retried, notRetryable }
    return retried === 0 ? { unchanged: true, result } : { next: queue, result }
  })

  if (counts.retried === 0) {
    // Nothing to do — and we say so, rather than flushing to manufacture the
    // APPEARANCE of an attempt.
    return { synced: 0, failed: 0, skipped: 'nothing-retryable', retried: 0, notRetryable: counts.notRetryable }
  }

  const flush = await flushQueue()
  return { ...flush, ...counts }
}

/**
 * The rep's escape hatch: remove ONE entry — but only once its payload is provably
 * safe somewhere other than this device.
 *
 * The capture is AWAITED and its real outcome asserted (`captureFailedWriteConfirmed`
 * throws on a non-2xx from the durable sink). If it does not confirm, we KEEP the
 * entry and say why. A Discard that silently ate the report when the capture failed
 * would be a worse bug than the one it is fixing.
 */
export async function discardEntry(id: string): Promise<DiscardOutcome> {
  // Read OUTSIDE the chain: the capture below is a network call, and holding the queue's
  // write chain across it would stall every enqueue behind a slow sink. The removal itself
  // re-reads the CURRENT queue inside the chain.
  const entry = (await readQueue()).find(e => e.id === id)
  if (!entry) return { discarded: false, reason: 'entry-not-found' }

  try {
    await captureFailedWriteConfirmed(entry.type, entry.payload, 'user-discarded', {
      id: entry.id,
      retries: entry.retries,
      createdAt: entry.createdAt,
      failureReason: entry.failureReason,
    })
  } catch (err) {
    const captureError = err instanceof Error ? `${err.name}: ${err.message}` : String(err)
    console.error(
      `[offlineQueue] discard REFUSED for ${id} — the durable off-device capture did not `
      + 'confirm, so the entry was KEPT. Nothing was deleted.',
      err,
    )
    return { discarded: false, reason: 'capture-not-confirmed', captureError }
  }

  return updateQueue<DiscardOutcome>(async queue => {
    const current = queue.find(e => e.id === id)
    // It left the queue while the capture was in flight (a flush delivered it, or another
    // discard won). Nothing to remove — and nothing re-created: an update never resurrects.
    if (!current) return { unchanged: true, result: { discarded: false, reason: 'entry-not-found' } }
    const remaining = queue.filter(e => e.id !== id)
    // A dependent that was waiting behind THIS entry (a v8 supplement behind its v2 visit
    // report) would be free to replay once it is gone — alone, and refused by the server.
    // It leaves the flush lane with its predecessor instead: pinned failed, KEPT, captured
    // off-device under the registration's discard reason (queueOrdering, fourth fate).
    for (const { dependent, reasonCode } of dependentsOrphanedByRemoval(current, remaining)) {
      applyPredecessorFailure(dependent, current, reasonCode, isCritical(dependent.type))
    }
    // A dependent that is NOT queued yet has nothing to pin: the discard is remembered by key, so
    // a later send of it is told "discarded" instead of reading the absent entry as landed. This
    // is a step on the DISCARD-LIST chain (a different key), never on this one — no deadlock.
    await recordDiscardedPredecessor(current, remaining)
    return { next: remaining, result: { discarded: true } }
  })
}
